import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { Client } from 'xrpl';
import { WALLET_STATUS } from '../types/wallet.js';
import { getBackendUrl } from '../config/environments.js';
import { 
  getWalletSession, 
  setWalletSession, 
  clearWalletSession, 
  isSessionValid,
  refreshSession,
  isBalanceCheckNeeded,
  setLastBalanceCheck
} from '../lib/auth.js';
import { 
  createSignInPayload, 
  subscribeToPayload, 
  openXummWallet,
  checkPayloadStatus 
} from '../lib/xumm.js';
import { 
  // All wallet_connections helpers removed in wallet-only migration
  // Functions now use API calls instead of direct Supabase access
} from '../lib/database.js';

/**
 * Initial wallet state
 */
const initialState = {
  // Connection status
  status: WALLET_STATUS.DISCONNECTED,
  isConnected: false,
  isLoading: false,
  
  // Wallet information
  walletAddress: null,
  displayName: null,
  
  // Token information
  xrpBalance: 0,
  tokenBalance: 0,
  hasRequiredTokens: false,
  
  // Verification status
  isVerifying: false,
  lastBalanceCheck: null,
  
  // Admin status
  isAdmin: false,
  
  // Error handling
  error: null,
  
  // XUMM payload tracking
  currentPayload: null,
  payloadSubscription: null,
  
  // XRPL client
  xrplClient: null
};

/**
 * Wallet reducer for state management
 */
const walletReducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload, error: null };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    
    case 'SET_WALLET_INFO':
      return {
        ...state,
        walletAddress: action.payload.address,
        displayName: action.payload.displayName,
        isConnected: true,
        status: WALLET_STATUS.CONNECTED,
        error: null
      };
    
    case 'SET_BALANCES':
      return {
        ...state,
        xrpBalance: action.payload.xrpBalance,
        tokenBalance: action.payload.tokenBalance,
        hasRequiredTokens: action.payload.hasRequiredTokens,
        lastBalanceCheck: action.payload.timestamp,
        isVerifying: false
      };
    
    case 'SET_VERIFYING':
      return { ...state, isVerifying: action.payload };
    
    case 'SET_ADMIN':
      return { ...state, isAdmin: action.payload };
    
    case 'SET_PAYLOAD':
      return { ...state, currentPayload: action.payload };
    
    case 'SET_SUBSCRIPTION':
      return { ...state, payloadSubscription: action.payload };
    
    case 'SET_XRPL_CLIENT':
      return { ...state, xrplClient: action.payload };
    
    case 'RESET_WALLET':
      return {
        ...initialState,
        xrplClient: state.xrplClient // Keep XRPL client connected
      };
    
    default:
      return state;
  }
};

/**
 * Wallet Context
 */
const WalletContext = createContext();

/**
 * Wallet Provider Component
 */
export const WalletProvider = ({ children }) => {
  const [state, dispatch] = useReducer(walletReducer, initialState);
  
  // Add connection throttling to prevent duplicate requests
  const lastConnectionAttempt = useRef(0);
  const CONNECTION_THROTTLE_MS = 2000; // 2 seconds

  /**
   * Initialize XRPL client
   */
  const initializeXrplClient = useCallback(async () => {
    if (state.xrplClient?.isConnected()) {
      return state.xrplClient;
    }

    try {
      const networkUrl = process.env.REACT_APP_XRPL_NETWORK || 'wss://xrplcluster.com';
      const client = new Client(networkUrl);
      await client.connect();
      
      dispatch({ type: 'SET_XRPL_CLIENT', payload: client });
      console.log('XRPL client connected to:', networkUrl);
      return client;
    } catch (error) {
      console.error('Failed to connect to XRPL:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to connect to XRPL network' });
      return null;
    }
  }, [state.xrplClient]);

  /**
   * Verify token balance and requirements
   */
  const verifyTokenBalance = useCallback(async (walletAddress) => {
    if (!walletAddress) {
      console.log('[DEBUG] No wallet address provided to verifyTokenBalance');
      return false;
    }

    dispatch({ type: 'SET_VERIFYING', payload: true });

    try {
      const client = await initializeXrplClient();
      if (!client) {
        console.log('[DEBUG] XRPL client not available');
        throw new Error('XRPL client not available');
      }

      // Get XRP balance
      const accountInfo = await client.request({
        command: 'account_info',
        account: walletAddress,
        ledger_index: 'validated'
      });

      const xrpBalance = Number(accountInfo.result.account_data.Balance) / 1000000;
      console.log('[DEBUG] XRP Balance:', xrpBalance, '| Raw:', accountInfo.result.account_data.Balance);
      console.log('[DEBUG] account_info result:', accountInfo);

      // Get token balance
      const currencyCode = process.env.REACT_APP_XGC_CURRENCY_CODE || 'XGC';
      const issuer = process.env.REACT_APP_XGC_ISSUER;
      const requiredAmount = Number(process.env.REACT_APP_REQUIRED_TOKEN_AMOUNT) || 100000;

      let tokenBalance = 0;
      let hasRequiredTokens = false;

      // Debug: Log all relevant config and wallet info
      console.log('[DEBUG] Wallet Address:', walletAddress);
      console.log('[DEBUG] Token Currency Code:', currencyCode, '| typeof:', typeof currencyCode);
      console.log('[DEBUG] Token Issuer:', issuer, '| typeof:', typeof issuer);
      console.log('[DEBUG] Required Token Amount:', requiredAmount, '| typeof:', typeof requiredAmount);

      if (issuer) {
        try {
          const accountLines = await client.request({
            command: 'account_lines',
            account: walletAddress,
            ledger_index: 'validated'
          });

          // Debug: Log all trustlines
          if (Array.isArray(accountLines.result.lines)) {
            console.log('[DEBUG] Trustlines:', accountLines.result.lines.map(l => ({currency: l.currency, issuer: l.account, balance: l.balance, limit: l.limit, limit_peer: l.limit_peer, quality_in: l.quality_in, quality_out: l.quality_out})));
          } else {
            console.log('[DEBUG] No trustlines found (lines is not array):', accountLines.result.lines);
          }

          console.log('[DEBUG] account_lines result:', accountLines);

          const tokenLine = accountLines.result.lines.find(
            line => line.currency === currencyCode && line.account === issuer
          );

          if (tokenLine) {
            tokenBalance = Number(tokenLine.balance);
            hasRequiredTokens = tokenBalance >= requiredAmount;
            // Debug: Log found token line
            console.log('[DEBUG] Found token trustline:', tokenLine);
          } else {
            console.warn('[DEBUG] No matching token trustline found for currency/issuer:', currencyCode, issuer);
          }
        } catch (tokenError) {
          console.warn('[DEBUG] Could not fetch token balance:', tokenError.message, tokenError);
        }
      } else {
        console.warn('[DEBUG] No issuer configured, cannot check token trustline.');
      }

      const balanceData = {
        xrpBalance,
        tokenBalance,
        hasRequiredTokens,
        timestamp: Date.now()
      };

      dispatch({ type: 'SET_BALANCES', payload: balanceData });
      setLastBalanceCheck();

      // Debug: Log final balance check result
      console.log('[DEBUG] Final balance data:', balanceData);

      return hasRequiredTokens;

    } catch (error) {
      console.error('[DEBUG] Failed to verify token balance:', error);
      dispatch({ type: 'SET_ERROR', payload: `Balance verification failed: ${error.message}` });
      dispatch({ type: 'SET_VERIFYING', payload: false });
      return false;
    }
  }, [initializeXrplClient]);

  /**
   * Clean up any existing connection attempts
   */
  const cleanupExistingConnection = useCallback(() => {
    console.log('Cleaning up existing connection attempts...');
    
    // Clear any timeouts first
    if (state.currentPayload?.timeoutId) {
      console.log('Clearing connection timeout');
      clearTimeout(state.currentPayload.timeoutId);
    }
    
    // Unsubscribe from any active subscription
    if (state.payloadSubscription) {
      console.log('Unsubscribing from existing payload subscription');
      state.payloadSubscription.unsubscribe();
      dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
    }
    
    // Clear current payload
    if (state.currentPayload) {
      console.log('Clearing existing payload:', state.currentPayload.uuid);
      dispatch({ type: 'SET_PAYLOAD', payload: null });
    }
    
    // Reset status if connecting
    if (state.status === WALLET_STATUS.CONNECTING) {
      console.log('Resetting connection status');
      dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.payloadSubscription, state.currentPayload, state.status]);

  /**
   * Connect wallet using XUMM
   */
  const connectWallet = useCallback(async () => {
    // Throttle connection attempts to prevent React StrictMode duplicates
    const now = Date.now();
    if (now - lastConnectionAttempt.current < CONNECTION_THROTTLE_MS) {
      console.warn('Connection attempt throttled - too soon after last attempt');
      return { success: false, error: 'Connection throttled' };
    }
    lastConnectionAttempt.current = now;

    // Clean up any existing connection attempts first
    cleanupExistingConnection();

    if (state.status === WALLET_STATUS.CONNECTING) {
      console.warn('Wallet connection already in progress');
      return { success: false, error: 'Connection already in progress' };
    }

    dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.CONNECTING });
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // Create sign-in payload
      const payloadResult = await createSignInPayload({
        expireMinutes: 10
      });

      if (!payloadResult.success) {
        throw new Error('Failed to create XUMM payload');
      }

      dispatch({ type: 'SET_PAYLOAD', payload: payloadResult });

      // Open XUMM wallet
      const popup = openXummWallet(payloadResult.deepLink);
      
      // Subscribe to payload updates
      const subscription = await subscribeToPayload(payloadResult.uuid, async (event) => {
        console.log('Payload event received:', event);

        if (event.status === 'resolved' && event.account) {
          // Successful connection
          try {
            // Wallet authentication now handled via API calls
            // Set wallet session directly from XUMM response
            setWalletSession(event.account, {
              displayName: event.account, // Use wallet address as display name
              sessionId: Date.now().toString() // Temporary session ID
            });

            // Update state
            dispatch({ 
              type: 'SET_WALLET_INFO', 
              payload: { 
                address: event.account,
                displayName: event.account // Use wallet address as display name
              }
            });

            // Admin status check removed in wallet-only migration
            // All admin privileges handled by backend API
            dispatch({ type: 'SET_ADMIN', payload: false });

            // Verify token balance
            await verifyTokenBalance(event.account);

            // Close popup if still open
            if (popup && !popup.closed) {
              popup.close();
            }

            // Clean up subscription and payload
            subscription.unsubscribe();
            dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
            dispatch({ type: 'SET_PAYLOAD', payload: null });

            console.log('Wallet connected successfully:', event.account);

          } catch (error) {
            console.error('Post-connection setup failed:', error);
            dispatch({ type: 'SET_ERROR', payload: `Connection setup failed: ${error.message}` });
          }

        } else if (event.status === 'rejected') {
          dispatch({ type: 'SET_ERROR', payload: 'Connection rejected by user' });
          dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
          subscription.unsubscribe();
          dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
          dispatch({ type: 'SET_PAYLOAD', payload: null });
          
        } else if (event.status === 'expired') {
          dispatch({ type: 'SET_ERROR', payload: 'Connection request expired' });
          dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
          subscription.unsubscribe();
          dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
          dispatch({ type: 'SET_PAYLOAD', payload: null });
          
        } else if (event.status === 'cancelled') {
          dispatch({ type: 'SET_ERROR', payload: 'Connection cancelled' });
          dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
          subscription.unsubscribe();
          dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
          dispatch({ type: 'SET_PAYLOAD', payload: null });
        }

        dispatch({ type: 'SET_LOADING', payload: false });
      });

      dispatch({ type: 'SET_SUBSCRIPTION', payload: subscription });

      // Set timeout for connection attempt with enhanced cleanup
      const connectionTimeout = setTimeout(() => {
        if (state.status === WALLET_STATUS.CONNECTING) {
          console.log('Connection timeout reached, cleaning up...');
          subscription.unsubscribe();
          dispatch({ type: 'SET_ERROR', payload: 'Connection timeout' });
          dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_SUBSCRIPTION', payload: null });
          dispatch({ type: 'SET_PAYLOAD', payload: null });
          
          // Close popup if still open
          if (popup && !popup.closed) {
            popup.close();
          }
        }
      }, 300000); // 5 minutes

      // Store timeout ID in payload for later cleanup
      dispatch({ 
        type: 'SET_PAYLOAD', 
        payload: { 
          ...payloadResult, 
          timeoutId: connectionTimeout 
        } 
      });

      return {
        success: true,
        qrUrl: payloadResult.qrUrl,
        deepLink: payloadResult.deepLink,
        uuid: payloadResult.uuid
      };

    } catch (error) {
      console.error('Wallet connection failed:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
      dispatch({ type: 'SET_LOADING', payload: false });
      
      return { success: false, error: error.message };
    }
  }, [state.status, verifyTokenBalance, cleanupExistingConnection]);

  /**
   * Cancel ongoing connection attempt
   */
  const cancelConnection = useCallback(async () => {
    console.log('Cancelling ongoing connection attempt...');
    
    try {
      // Clean up any existing connection state
      cleanupExistingConnection();
      
      // Clear any timeouts
      if (state.currentPayload?.timeoutId) {
        clearTimeout(state.currentPayload.timeoutId);
      }
      
      // Try to cancel the payload on the backend if we have a UUID
      if (state.currentPayload?.uuid) {
        try {
          console.log('Attempting to cancel payload on backend:', state.currentPayload.uuid);
          const response = await fetch(`${getBackendUrl()}/api/xumm/payload/${state.currentPayload.uuid}`, {
            method: 'DELETE'
          });
          
          if (response.ok) {
            console.log('Successfully cancelled payload on backend');
          } else {
            console.warn('Failed to cancel payload on backend:', response.status);
          }
        } catch (error) {
          console.warn('Error cancelling payload on backend:', error.message);
        }
      }
      
      // Reset to disconnected state
      dispatch({ type: 'SET_STATUS', payload: WALLET_STATUS.DISCONNECTED });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      return { success: true };
      
    } catch (error) {
      console.error('Error cancelling connection:', error);
      return { success: false, error: error.message };
    }
  }, [cleanupExistingConnection, state.currentPayload]);

  /**
   * Disconnect wallet
   */
  const disconnectWallet = useCallback(async () => {
    try {
      // Database connection management removed in wallet-only migration
      // All session cleanup handled locally
      console.log('Disconnecting wallet:', state.walletAddress);

      // Clean up subscription
      if (state.payloadSubscription) {
        state.payloadSubscription.unsubscribe();
      }

      // Clear session storage
      clearWalletSession();

      // Reset state
      dispatch({ type: 'RESET_WALLET' });

      console.log('Wallet disconnected successfully');
      return { success: true };

    } catch (error) {
      console.error('Wallet disconnection failed:', error);
      return { success: false, error: error.message };
    }
  }, [state.walletAddress, state.payloadSubscription]);

  /**
   * Refresh wallet connection
   */
  const refreshConnection = useCallback(async () => {
    if (!state.walletAddress) return false;

    try {
      // Refresh session
      refreshSession(state.walletAddress);

      // Check if balance verification is needed
      if (isBalanceCheckNeeded()) {
        await verifyTokenBalance(state.walletAddress);
      }

      return true;
    } catch (error) {
      console.error('Failed to refresh connection:', error);
      return false;
    }
  }, [state.walletAddress, verifyTokenBalance]);

  /**
   * Initialize wallet on app start
   */
  useEffect(() => {
    const initializeWallet = async () => {
      try {
        // Initialize XRPL client
        await initializeXrplClient();

        // Check for existing session
        const session = getWalletSession();
        if (session && isSessionValid(session)) {
          console.log('Restoring wallet session:', session.walletAddress);

          // Restore wallet state
          dispatch({ 
            type: 'SET_WALLET_INFO', 
            payload: { 
              address: session.walletAddress,
              displayName: session.displayName
            }
          });

          // Admin status check removed in wallet-only migration
          // All admin privileges handled by backend API
          dispatch({ type: 'SET_ADMIN', payload: false });

          // Verify token balance if needed
          if (isBalanceCheckNeeded()) {
            await verifyTokenBalance(session.walletAddress);
          }

        } else if (session) {
          // Invalid session, clear it
          console.log('Invalid session found, clearing...');
          clearWalletSession();
        }

      } catch (error) {
        console.error('Wallet initialization failed:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Wallet initialization failed' });
      }
    };

    initializeWallet();
  }, [initializeXrplClient, verifyTokenBalance]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (state.payloadSubscription) {
        state.payloadSubscription.unsubscribe();
      }
      if (state.xrplClient?.isConnected()) {
        state.xrplClient.disconnect();
      }
    };
  }, [state.payloadSubscription, state.xrplClient]);

  /**
   * Context value
   */
  const contextValue = {
    // State
    ...state,
    
    // Actions
    connectWallet,
    disconnectWallet,
    cancelConnection,
    cleanupExistingConnection,
    refreshConnection,
    verifyTokenBalance: () => verifyTokenBalance(state.walletAddress),
    
    // Utilities
    isConnected: state.isConnected && state.walletAddress,
    truncatedAddress: state.walletAddress ? 
      `${state.walletAddress.substring(0, 6)}...${state.walletAddress.substring(-4)}` : null
  };

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

/**
 * Hook to use wallet context
 */
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export default WalletContext; 